import {
  JWT_COOKIE,
  clearJwtCookies,
  fetchJwtForShop,
  setJwtCookies,
  storedJwtUsableForShop,
} from "@/lib/jwt-shop-scope";
import { decodeJWT } from "@/lib/utils";
import { fetchCustomerCompanies } from "@/networking/auth.api";
import Cookies from "js-cookie";
import { useRouter, useSearchParams } from "next/navigation";
import { useMemo, useEffect, useRef } from "react";

// expectedShop is the fluid_shop (company subdomain) the surrounding page is
// scoped to — for checkout pages, the cart's company subdomain. When given,
// a stored JWT bound to a different shop is never used directly; the hook
// first tries to exchange it for a token scoped to the current shop. Callers
// without a shop context (management, order pages) omit it and keep the
// previous behavior.
function useJwt(expectedShop?: string | null) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const rebindingShopRef = useRef<string | null>(null);

  // Handle JWT from URL parameter side effect. Backend-built checkout links
  // carry fluid_shop alongside jwt (Commerce::Cart#checkout_query_params), and
  // the in-app login redirects include it too, so the binding is written with
  // the token. A jwt arriving without fluid_shop is stored unbound and will be
  // treated as stale by any shop-scoped reader.
  useEffect(() => {
    const rawJwt = searchParams?.get("jwt");

    if (rawJwt && decodeJWT(rawJwt)) {
      setJwtCookies(rawJwt, searchParams?.get("fluid_shop"));
    }

    if (rawJwt) {
      const newSearchParams = new URLSearchParams(searchParams?.toString());
      newSearchParams.delete("jwt");
      const queryString = newSearchParams.toString();
      router.replace(
        `${window.location.pathname}${queryString ? `?${queryString}` : ""}`,
      );
    }
  }, [searchParams, router]);

  // Derive JWT values from cookies/URL. A jwt in the URL is always honored —
  // it arrives together with its fluid_shop param on entry links and the
  // effect above binds it; only cookie reads are gated by the shop binding.
  const { jwt, decodedJwt } = useMemo(() => {
    const rawJwt = searchParams?.get("jwt");
    const cookieJwt = !rawJwt ? Cookies.get(JWT_COOKIE) : null;
    const usableCookieJwt =
      cookieJwt && storedJwtUsableForShop(expectedShop) ? cookieJwt : null;

    const finalJwt = rawJwt || usableCookieJwt;
    const decoded = finalJwt ? decodeJWT(finalJwt) : null;

    if (finalJwt && !decoded) {
      return { jwt: null, decodedJwt: null };
    }

    return { jwt: finalJwt, decodedJwt: decoded };
  }, [searchParams, expectedShop]);

  // Self-heal: a cookie bound to another shop cannot be presented to this
  // checkout, but the same Fluid Pay account may have access to the target
  // shop. Ask the backend for a shop-scoped JWT first; only clear the stored
  // token when no matching company token is available. Reload after either
  // path so every consumer re-reads the cookie-backed auth state.
  useEffect(() => {
    if (!expectedShop) return;
    const cookieJwt = Cookies.get(JWT_COOKIE);
    if (!cookieJwt) return;
    if (storedJwtUsableForShop(expectedShop)) {
      rebindingShopRef.current = null;
      return;
    }

    if (rebindingShopRef.current === expectedShop) return;
    rebindingShopRef.current = expectedShop;

    let canceled = false;

    fetchJwtForShop({
      currentJwt: cookieJwt,
      expectedShop,
      fetchCustomerCompanies,
    })
      .then((jwtForShop) => {
        if (canceled) return;

        if (jwtForShop) {
          setJwtCookies(jwtForShop, expectedShop);
        } else {
          clearJwtCookies();
        }
      })
      .catch(() => {
        if (canceled) return;

        clearJwtCookies();
      })
      .finally(() => {
        if (canceled) return;

        // Reload after either path so every consumer re-reads the
        // cookie-backed auth state — cookie reads are not reactive.
        window.location.reload();
      });

    return () => {
      canceled = true;
      rebindingShopRef.current = null;
    };
  }, [expectedShop]);

  // Clean up invalid/expired JWT cookie
  useEffect(() => {
    const cookieJwt = Cookies.get(JWT_COOKIE);
    if (cookieJwt && !decodeJWT(cookieJwt)) {
      clearJwtCookies();
    }
  }, []);

  return { jwt, decodedJwt };
}

export default useJwt;
